<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import type {
  ISatMapMarkerEmits,
  ISatDrawerProps,
  ISatMapProps,
} from "@chatfood/design-system";
import {
  useImage,
  useTogglable,
  AtomCard,
  AtomIcon,
  AtomText,
  AtomButton,
  SatMap,
  SatDrawer,
  SatIconButton,
  SatMapMarkerInfo,
} from "@chatfood/design-system";
import { ref, computed, onMounted } from "vue";
import { useBusinessStore } from "~/stores/business";
import { useGeoIpLocation } from "~/stores/geoIpLocation";
import { usePublicConfig } from "~/config";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainOutletPickup } from "~/domain/outlet-pickup";
import { useNavigation } from "~/composables/use-navigation";
import { useDelivery } from "~/composables/use-delivery";
import { useBrandingStore } from "~/stores/branding";
import { useRichCard } from "~/composables/use-rich-card";
import { useLocale } from "~/composables/use-locale";
import { usePickup } from "~/composables/use-pickup";
import { useWindowResize } from "~/composables/use-window-resize";
import { NewAddress, type INewAddressEmits } from "~/components/new-address";
import { TheMobileHeader } from "~/components/the-mobile-header";
import { LocationInfo } from "~/components/location-info";
import { AddressBook } from "~/components/address-book";
import { PickupLocations } from "~/components/pickup-locations";
import { EnableLocation } from "~/components/enable-location";
import { BASE_TELEPORT_SELECTOR } from "~/domain/base-teleport-selector";
import { useHeaderHeight } from "~/composables/use-header-height";
import { useModalHubStore } from "~/components/modal-hub";
import type { IDomainCoordinate } from "~/domain/coordinate";

const css = bemBuilder("pages-select-mode");

const props = defineProps<{
  orderingMode?: IDomainOrderingMode;
}>();

const { t } = useLocale();
const { push } = useNavigation();
const { isMobile } = useWindowResize();
const { optimize } = useImage();

const { headerHeightPixel } = useHeaderHeight();
const business = useBusinessStore();
const brandingStore = useBrandingStore();
const modalHub = useModalHubStore();
const geoIpLocation = useGeoIpLocation();
const config = usePublicConfig();

const maxHeight = computed(() => {
  return `calc(100dvh - ${headerHeightPixel.value})`;
});

const marketingBanner = computed(() => {
  return brandingStore.getMediaUrl("marketing_cover_desktop");
});

const title = t("pages.home.seo_title", {
  businessName: business.name,
});

const description = t("pages.home.seo_description", {
  businessName: business.name,
});

const image = business.cover ?? business.logo;

useRichCard({ title, description, image });

const {
  state: isMapDrawerVisible,
  enable: showMapDrawer,
  disable: hideMapDrawer,
} = useTogglable();

const mapWrapperConfig = computed<{
  name: string | typeof SatDrawer;
  binds: ISatDrawerProps | object;
}>(() => {
  if (!isMobile.value) {
    return {
      name: "div",
      binds: {},
    };
  }

  return {
    name: SatDrawer,
    binds: {
      fullHeight: true,
    },
  };
});

const computedOrderingMode = computed(() => {
  return props.orderingMode || business.defaultOrderingMode;
});

const onOrderingModeSelected = (mode: IDomainOrderingMode) => {
  if (business.isMobileApp) {
    mobileAppIsSelectingMode.value = false;
  }
  push("order", { orderingMode: mode });
};

const {
  mapBoundaries: pickupMapBoundaries,
  locations,
  clickedLocation,
  isLoading: isPickupLoading,
  currentGeoLocation: pickupCurrentGeoLocation,
  onLocationsLoaded,
  onLocationClicked,
  onLocationBlur,
  onLocationFocused,
  updateMapBoundaries,
  onPickupAt,
  setCurrentGeoLocation: setPickupGeoLocation,
} = usePickup();

const {
  mapBoundaries: deliveryMapBoundaries,
  isLoading: isDeliveryLoading,
  deliveryAddress,
  deliveryLocation,
  currentGeoLocation: deliveryCurrentGeoLocation,
  onDeliveryLocationSelected,
  onUncoveredDeliveryLocationSelected,
  onAddressSelected,
  onDeliverTo,
  getCoordinateCoverage,
  onRefocusRegion,
  setCurrentGeoLocation: setDeliveryGeoLocation,
  setDeliveryLocationCoordinates,
} = useDelivery();

const onDeliveryLocationSelectedWrapper = (
  location:
    | INewAddressEmits["uncoveredAddressSelected"][0]
    | INewAddressEmits["select"][0],
) => {
  showMapDrawer();

  if (location.canDeliver) {
    onDeliveryLocationSelected(location);
    return;
  }

  onUncoveredDeliveryLocationSelected(location);
};

const displayMarketingBanner = computed(() => {
  return (
    !clickedLocation.value &&
    !deliveryLocation.value &&
    !deliveryAddress.value &&
    Boolean(marketingBanner.value)
  );
});

const mapBoundaries = computed<ISatMapProps["mapBounds"]>(() => {
  return computedOrderingMode.value === "delivery"
    ? deliveryMapBoundaries.value
    : pickupMapBoundaries.value;
});

const onLocationLookup = async (
  location: ISatMapMarkerEmits["addressLookup"][0],
) => {
  const coordinates =
    deliveryLocation.value?.coordinates || location.coordinates;
  const coverage = await getCoordinateCoverage(coordinates);

  if (!coverage) {
    onUncoveredDeliveryLocationSelected(
      {
        ...location,
        coordinates,
        canDeliver: false,
      },
      true,
    );

    return;
  }

  onDeliveryLocationSelected(
    {
      ...location,
      coordinates,
      canDeliver: true,
      deliveryCoverage: coverage,
    },
    true,
  );
};

const btnModesVariant = (mode: IDomainOrderingMode) => {
  return computedOrderingMode.value === mode ? "primary" : "secondary";
};

const btnModesTheme = (mode: IDomainOrderingMode) => {
  return computedOrderingMode.value === mode ? "filled" : "tailored";
};

const mobileAppIsSelectingMode = ref(true);

const mobileAppHeaderTitle = computed(() => {
  if (mobileAppIsSelectingMode.value) {
    return t("components.order_mode_setter.order");
  }

  if (computedOrderingMode.value === "pickup") {
    return t("components.order_mode_setter.pickup_title");
  }

  return t("components.order_mode_setter.delivery_title");
});

const mobileAppBackAction = () => {
  mobileAppIsSelectingMode.value = true;
  push("order", {});
};

const enableLocationDismissed = ref(false);

const currentGeoLocation = computed(() => {
  return computedOrderingMode.value === "delivery"
    ? deliveryCurrentGeoLocation.value
    : pickupCurrentGeoLocation.value;
});

const showEnableLocation = computed(() => {
  return (
    computedOrderingMode.value === "pickup" &&
    !clickedLocation.value &&
    !currentGeoLocation.value &&
    !enableLocationDismissed.value &&
    typeof window !== "undefined"
  );
});

const onGeoLocationObtained = (coordinate: IDomainCoordinate) => {
  setPickupGeoLocation(coordinate);
  setDeliveryGeoLocation(coordinate);
};

const dismissEnableLocation = () => {
  enableLocationDismissed.value = true;
};

const showLocationInfo = ref(false);

const handleCloseLocationInfo = () => {
  showLocationInfo.value = false;
  clickedLocation.value = undefined;
};

const onSelectedLocationInfo = (locationId: string) => {
  const location = locations.value.find((loc) => loc.outletId === locationId);
  if (location) {
    onLocationClicked(location);
    showLocationInfo.value = true;
  }
};

const handleLocationClicked = (location: IDomainOutletPickup) => {
  showLocationInfo.value = false;
  onLocationClicked(location);
};

const handleOrderingModeUnavailable = () => {
  if (!business.hasDelivery && !business.hasPickup) {
    modalHub.showWarning({
      title: t("pages.select_mode.warn_ordering_mode_title"),
      message: t("pages.select_mode.warn_ordering_mode_message"),
      confirmLabel: t("pages.select_mode.warn_ordering_mode_confirm"),
      onWarningConfirm: () => modalHub.hideWarning(),
    });
  }
};

onMounted(() => {
  if (geoIpLocation.searchablePlace) {
    onRefocusRegion(geoIpLocation.searchablePlace);
  }

  handleOrderingModeUnavailable();
});
</script>

<template>
  <div :class="[css(''), business.isMobileApp && css('', 'mobile-app')]">
    <AtomCard padding="none" elevation="small" :class="css('mode-setter')">
      <div v-if="!business.isMobileApp && business.cover">
        <div
          :class="css('cover')"
          :style="`background-image: url(${optimize(business.cover, {
            w: 800,
            h: 450,
            fit: 'crop',
          })})`"
        />
      </div>
      <div v-if="business.isMobileApp">
        <TheMobileHeader
          no-shadow
          hide-actions
          :title="mobileAppHeaderTitle"
          :hide-cta="mobileAppIsSelectingMode"
          :cta-action="mobileAppBackAction"
        />
        <div v-if="mobileAppIsSelectingMode" :class="css('mobile-order-mode')">
          <button
            v-if="business.hasDelivery"
            :class="css('mobile-order-mode-cta')"
            @click="onOrderingModeSelected('delivery')"
          >
            <AtomIcon name="scooter" :size="24" />
            <div>
              <AtomText>
                {{ t("components.order_mode_setter.delivery") }}
              </AtomText>
              <AtomText tag="div" type="small" color="color-text-secondary">
                {{ t("components.order_mode_setter.delivery_desc") }}
              </AtomText>
            </div>
          </button>
          <button
            v-if="business.hasPickup"
            :class="css('mobile-order-mode-cta')"
            @click="onOrderingModeSelected('pickup')"
          >
            <AtomIcon name="pin" :size="24" />
            <div>
              <AtomText>
                {{ t("components.order_mode_setter.pickup") }}
              </AtomText>
              <AtomText tag="div" type="small" color="color-text-secondary">
                {{ t("components.order_mode_setter.pickup_desc") }}
              </AtomText>
            </div>
          </button>
        </div>
      </div>
      <div
        v-else-if="business.hasDelivery && business.hasPickup"
        :class="css('delivery-modes')"
      >
        <div
          :class="{
            [css('delivery-modes-selector')]: true,
            [css('delivery-modes-selector', 'pickup')]:
              business.defaultOrderingMode === 'pickup',
          }"
        >
          <AtomButton
            wide
            :variant="btnModesVariant('delivery')"
            :theme="btnModesTheme('delivery')"
            @click="onOrderingModeSelected('delivery')"
          >
            {{ t("components.order_mode_setter.delivery") }}
          </AtomButton>
          <AtomButton
            wide
            :variant="btnModesVariant('pickup')"
            :theme="btnModesTheme('pickup')"
            @click="onOrderingModeSelected('pickup')"
          >
            {{ t("components.order_mode_setter.pickup") }}
          </AtomButton>
        </div>
      </div>
      <div
        v-if="!business.isMobileApp || !mobileAppIsSelectingMode"
        :class="css('content')"
      >
        <KeepAlive>
          <div v-if="computedOrderingMode === 'delivery'">
            <div :class="css('new-address')">
              <AtomText type="subtitle">
                {{ t("components.order_mode_setter.new_address_title") }}
              </AtomText>
              <NewAddress
                @select="onDeliveryLocationSelectedWrapper"
                @uncovered-address-selected="onDeliveryLocationSelectedWrapper"
              />
            </div>
            <AddressBook
              :active-address-id="deliveryAddress?.id"
              :is-cta-loading="isDeliveryLoading"
              @selected="(address) => onAddressSelected(address)"
              @deliver="
                (address) =>
                  onDeliverTo(address, {
                    lat: address.lat,
                    lng: address.lng,
                    addressId: address?.id,
                    address: address?.location,
                    number: address?.number,
                    city: address?.city,
                    country: business.country,
                    postalCode: address?.zipCode,
                    instructions: address?.instruction,
                  })
              "
            />
          </div>
        </KeepAlive>
        <KeepAlive>
          <PickupLocations
            v-if="computedOrderingMode === 'pickup'"
            :is-cta-loading="isPickupLoading"
            :clicked-location-id="clickedLocation?.outletId"
            :current-geo-location="pickupCurrentGeoLocation"
            @selected-location="(location) => onPickupAt(location.url)"
            @selected-location-id="onSelectedLocationInfo"
            @loaded-locations="onLocationsLoaded"
            @clicked-location="handleLocationClicked"
            @focused-location="onLocationFocused"
            @geo-location-obtained="onGeoLocationObtained"
            @recenter-map="updateMapBoundaries"
          />
        </KeepAlive>
      </div>
    </AtomCard>
    <AtomCard
      :class="[
        css('info-bar'),
        showLocationInfo &&
          clickedLocation?.outletId &&
          css('info-bar', 'visible'),
      ]"
      radii="none"
      elevation="none"
      padding="none"
    >
      <LocationInfo
        v-if="showLocationInfo && clickedLocation?.outletId"
        :outlet-id="clickedLocation.outletId"
        @close="handleCloseLocationInfo"
      />
    </AtomCard>
    <component
      :is="mapWrapperConfig.name"
      v-model="isMapDrawerVisible"
      v-bind="mapWrapperConfig.binds"
      :class="css('map-container')"
    >
      <div
        v-if="displayMarketingBanner && marketingBanner"
        :class="css('business-cover')"
        :style="`background-image: url(${optimize(marketingBanner, {
          w: 1200,
          fit: 'crop',
        })})`"
      />
      <div :class="css('map-wrapper')">
        <div :class="css('map-header')">
          <TheMobileHeader
            no-shadow
            hide-actions
            :cta-action="hideMapDrawer"
            :title="t('pages.home.drawer_map_title')"
          />
          <NewAddress
            :class="css('map-header-search')"
            @select="onDeliveryLocationSelectedWrapper"
            @uncovered-address-selected="onDeliveryLocationSelectedWrapper"
          />
        </div>
        <SatMap
          :class="css('map')"
          :api-key="config.googleMapsApiKey"
          :country="business.country"
          :map-bounds="mapBoundaries"
          :options="{
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            clickableIcons: false,
            mapTypeControl: false,
            zoomControl: false,
            streetViewControl: false,
          }"
        >
          <SatMapMarkerInfo
            v-if="deliveryLocation"
            :is-visible="computedOrderingMode === 'delivery'"
            :draggable="true"
            :position="{
              lat: deliveryLocation.coordinates.lat,
              lng: deliveryLocation.coordinates.lng,
            }"
            :title="
              deliveryLocation.canDeliver
                ? (deliveryLocation.city ?? '')
                : t('pages.home.location_uncovered')
            "
            :description="deliveryLocation.formattedAddress ?? ''"
            :marker-color="business.designTokens['color-primary']"
            :cta-label="t('pages.home.delivery_cta_map_label')"
            :is-cta-loading="isDeliveryLoading"
            :is-info-disabled="!deliveryLocation.canDeliver"
            :lookup-address="true"
            :show-cta="!isMobile"
            @address-lookup="onLocationLookup"
            @drag-end="setDeliveryLocationCoordinates"
            @cta-click="
              () =>
                deliveryLocation &&
                deliveryLocation.canDeliver &&
                onDeliverTo(deliveryLocation, {
                  lat: deliveryLocation.coordinates.lat,
                  lng: deliveryLocation.coordinates.lng,
                  address: deliveryLocation.formattedAddress ?? '',
                  city: deliveryLocation.city,
                  number: deliveryLocation.streetNumber,
                  country: deliveryLocation.country ?? business.country,
                  postalCode: deliveryLocation.postalCode,
                })
            "
          >
            <teleport :to="BASE_TELEPORT_SELECTOR">
              <div v-if="isMobile" :class="css('cta-mobile')">
                <AtomButton
                  wide
                  size="large"
                  :is-loading="isDeliveryLoading"
                  :is-disabled="!deliveryLocation.canDeliver"
                  @click="
                    () =>
                      deliveryLocation &&
                      deliveryLocation.canDeliver &&
                      onDeliverTo(deliveryLocation, {
                        lat: deliveryLocation.coordinates.lat,
                        lng: deliveryLocation.coordinates.lng,
                        address: deliveryLocation.formattedAddress ?? '',
                        city: deliveryLocation.city,
                        number: deliveryLocation.streetNumber,
                        country: deliveryLocation.country ?? business.country,
                        postalCode: deliveryLocation.postalCode,
                      })
                  "
                >
                  {{ t("pages.home.delivery_cta_map_label") }}
                </AtomButton>
              </div>
            </teleport>
          </SatMapMarkerInfo>
          <SatMapMarkerInfo
            v-if="deliveryAddress"
            :is-visible="computedOrderingMode === 'delivery'"
            :position="{
              lat: deliveryAddress.lat,
              lng: deliveryAddress.lng,
            }"
            :title="deliveryAddress.nickname"
            :description="deliveryAddress.location"
            :marker-color="business.designTokens['color-primary']"
            :cta-label="t('pages.home.delivery_cta_map_label')"
            :is-cta-loading="isDeliveryLoading"
            @cta-click="
              () =>
                deliveryAddress &&
                onDeliverTo(deliveryAddress, {
                  lat: deliveryAddress.lat,
                  lng: deliveryAddress.lng,
                  address: deliveryAddress?.location,
                  addressId: deliveryAddress.id,
                  number: deliveryAddress.number,
                  country: business.country,
                  postalCode: deliveryAddress?.zipCode,
                  instructions: deliveryAddress?.instruction,
                })
            "
          />
          <div v-if="showEnableLocation" :class="css('enable-location')">
            <SatIconButton
              size="small"
              icon-name="close"
              :class="css('enable-location-close-button')"
              @click="dismissEnableLocation"
            />
            <EnableLocation @geo-location-obtained="onGeoLocationObtained" />
          </div>
          <template v-else>
            <SatMapMarkerInfo
              v-for="(location, index) in locations"
              :key="location.outletId"
              :is-visible="computedOrderingMode === 'pickup'"
              :position="{
                lat: location.coordinates.lat,
                lng: location.coordinates.lng,
              }"
              :title="location.name"
              :description="location.location"
              :marker-color="business.designTokens['color-primary']"
              :label="`${index + 1}`"
              :is-info-open="location.isFocused || location.isSelected"
              :show-cta="location.state !== 'closed' && location.isSelected"
              :is-cta-loading="isPickupLoading"
              :cta-label="
                location.state === 'open'
                  ? t('pages.home.pickup_cta_map_label')
                  : t('pages.home.schedule_cta_map_label')
              "
              @mouse-over="onLocationFocused(location)"
              @mouse-out="onLocationBlur()"
              @marker-click="handleLocationClicked(location)"
              @cta-click="onPickupAt(location.url)"
            />
          </template>
        </SatMap>
      </div>
    </component>
  </div>
</template>

<style lang="scss" scoped>
.pages-select-mode {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;

  &__mode-setter {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media (width >= $breakpoint-sm) {
      overflow-y: auto;
      max-height: v-bind(maxHeight);
    }
  }

  &__cover {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    aspect-ratio: auto 16 / 9;
    border-radius: 0;

    @media (width >= $breakpoint-sm) {
      border-radius: var(--radii-card-md) var(--radii-card-md) 0 0;
    }
  }

  &__mobile-order-mode {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    padding: var(--spacing-lg);

    &-cta {
      width: 100%;
      display: flex;
      cursor: pointer;
      text-align: start;
      align-items: flex-start;
      gap: var(--spacing-sm);
      padding: var(--spacing-md);
      border-radius: var(--radii-card-sm);
      background: var(--color-base-100);
      border: solid 1px var(--color-base-60);

      &:active {
        background: var(--color-base-95);
        border-color: var(--color-base-40);
      }
    }
  }

  &__delivery-modes {
    padding: var(--spacing-lg) var(--spacing-lg) 0;

    &-selector {
      display: flex;
      gap: var(--spacing-md);
      padding: var(--spacing-xs);
      border-bottom: solid 1px var(--color-base-95);

      &--pickup {
        flex-direction: row-reverse;
      }
    }
  }

  &__content {
    display: flex;
    gap: var(--spacing-lg);
    flex-direction: column;
    padding: var(--spacing-lg);
  }

  &__new-address {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    padding-bottom: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);
    border-bottom: solid 1px var(--color-base-95);
  }

  &__map-container {
    width: 100%;
    flex: 1;
  }

  &__map-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    @media (width >= $breakpoint-sm) {
      flex-direction: initial;
    }
  }

  &__map-header {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media (width >= $breakpoint-sm) {
      display: none;
    }
  }

  &__map-header-search {
    padding-inline: var(--spacing-md);
    padding-bottom: var(--spacing-md);
  }

  &__map {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: auto;

    @media (width >= $breakpoint-sm) {
      min-height: 100%;
    }
  }

  &__enable-location {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    z-index: 9;
    transform: translate(-50%, -50%);
  }

  &__enable-location-close-button {
    position: absolute;
    left: calc(100% + var(--spacing-xs));
  }

  &__info-bar {
    display: flex;
    align-items: flex-start;
    max-width: 0;
    width: 0;
    padding: 0;
    overflow: hidden;
    transition:
      max-width 0.3s ease-in-out,
      width 0.3s ease-in-out;

    &--visible {
      max-width: 320px;
      padding: var(--spacing-lg);
      width: 100%;
    }
  }

  &__cta-mobile {
    position: fixed;
    width: 100%;
    bottom: 0;
    padding: var(--spacing-md);
    z-index: 999;
  }

  &:not(.pages-select-mode--mobile-app) {
    @media (width >= $breakpoint-sm) {
      display: flex;
      height: 100%;
    }

    .pages-select-mode {
      &__delivery-modes-selector {
        border-bottom: none;
        gap: var(--spacing-xs);
        border-radius: var(--radii-button);
        background-color: var(--color-secondary);
      }

      &__new-address {
        padding: 0;
        border-bottom: none;
      }

      &__cover {
        @media (width >= $breakpoint-sm) {
          display: none;
        }
      }

      &__mode-setter {
        max-width: 576px;
        width: 100%;
        position: initial;
        border-radius: 0;
        border-right: 1px solid var(--color-base-90);
      }

      &__map-container {
        @media (width >= $breakpoint-sm) {
          position: relative;
        }
      }

      &__business-cover {
        display: flex;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        background-size: cover;
        background-position: center;
      }
    }

    :deep(.wo-location--is-active),
    :deep(.wo-location:hover) {
      box-shadow: none;
    }
  }
}
</style>
